/**
 * Enhanced Field Types for Dynamic Forms
 * 
 * React components for the new field types added to the dynamic form system:
 * - TIME: Time picker field
 * - DATETIME: Date and time picker field  
 * - SIGNATURE: Digital signature pad
 * - HEADER: Informational header/title
 * - PARAGRAPH: Informational paragraph text
 * - DIAGRAM: Diagram for annotation
 * - BARCODE: Barcode generation/display
 * - PHONE: Phone number input
 * - URL: URL input
 */

import React, { useState, useRef, useEffect } from 'react';
import { Form, Alert, Button, Card } from 'react-bootstrap';
import { FaSignature, FaClock, FaCalendarAlt, FaPhone, FaLink, FaQrcode, FaImage } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Time Field Component
export const TimeField = ({ field, value, onChange, error }) => {
  const handleTimeChange = (e) => {
    onChange(field.field_id, e.target.value);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaClock className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        type="time"
        value={(value !== undefined && value !== null) ? value : ''}
        onChange={handleTimeChange}
        isInvalid={!!error}
        placeholder={field.placeholder}
        required={field.required}
      />
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// DateTime Field Component
export const DateTimeField = ({ field, value, onChange, error }) => {
  const handleDateTimeChange = (e) => {
    onChange(field.field_id, e.target.value);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaCalendarAlt className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        type="datetime-local"
        value={(value !== undefined && value !== null) ? value : ''}
        onChange={handleDateTimeChange}
        isInvalid={!!error}
        placeholder={field.placeholder}
        required={field.required}
      />
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Signature Field Component
export const SignatureField = ({ field, value, onChange, error, disabled }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    // Skip canvas setup if in disabled/read-only mode
    if (disabled) return;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';

      // Load existing signature if any
      if (value) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          setHasSignature(true);
        };
        img.src = value;
      }
    }
  }, [value, disabled]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL();
      onChange(field.field_id, dataURL);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange(field.field_id, '');
  };

  // Render read-only mode (display signature as image)
  if (disabled && value) {
    return (
      <Form.Group className="mb-3">
        <Form.Label className="field-label">
          <FaSignature className="me-2" />
          {field.label}
        </Form.Label>
        {field.help_text && (
          <div className="field-help-text" style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
            {field.help_text}
          </div>
        )}
        <div className="signature-display">
          <img
            src={value}
            alt="Signature"
            style={{
              maxWidth: '100%',
              height: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              display: 'block',
              backgroundColor: '#f8f9fa'
            }}
          />
        </div>
      </Form.Group>
    );
  }

  // Render read-only mode without signature
  if (disabled && !value) {
    return (
      <Form.Group className="mb-3">
        <Form.Label className="field-label">
          <FaSignature className="me-2" />
          {field.label}
        </Form.Label>
        <div className="text-muted" style={{ fontStyle: 'italic' }}>
          No signature provided
        </div>
      </Form.Group>
    );
  }

  // Render editable mode (canvas for drawing)
  return (
    <Form.Group className="mb-3">
      <Form.Label className="field-label">
        <FaSignature className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      {field.help_text && (
        <div className="field-help-text" style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
          {field.help_text}
        </div>
      )}
      <div className="signature-pad-container">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          style={{
            border: '1px solid #dee2e6',
            cursor: 'crosshair',
            maxWidth: '100%',
            display: 'block',
            backgroundColor: '#fff'
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
        <div className="mt-2">
          <Button variant="outline-primary" size="sm" onClick={clearSignature}>
            Clear Signature
          </Button>
        </div>
      </div>
      {error && Array.isArray(error) && error.length > 0 && (
        <div className="invalid-feedback d-block">
          {error.map((err, idx) => (
            <div key={idx}>{err.message}</div>
          ))}
        </div>
      )}
      {error && !Array.isArray(error) && (
        <div className="invalid-feedback d-block">{error}</div>
      )}
      {field.required && !hasSignature && (
        <div className="invalid-feedback d-block">Signature is required</div>
      )}
    </Form.Group>
  );
};

// Header Field Component
export const HeaderField = ({ field }) => {
  return (
    <div className="mb-3">
      <div className="section-header">
        <h4 className="section-title">{field.label}</h4>
        <div className="section-divider"></div>
      </div>
      {field.help_text && (
        <div className="text-muted mt-2">{field.help_text}</div>
      )}
    </div>
  );
};

// Markdown Field Component
export const MarkdownField = ({ field }) => {
  return (
    <div className="mb-3 markdown-field-container">
      <div className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Open links in new tab with security
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
            // Responsive images
            img: ({ node, ...props }) => (
              <img
                {...props}
                style={{ maxWidth: '100%', height: 'auto' }}
                loading="lazy"
              />
            ),
          }}
        >
          {field.label || ''}
        </ReactMarkdown>
      </div>
      {field.help_text && (
        <div className="text-muted mt-2 small" style={{ fontStyle: 'italic' }}>
          {field.help_text}
        </div>
      )}
    </div>
  );
};

// Paragraph Field Component
export const ParagraphField = ({ field, value, onChange, error, disabled }) => {
  const handleParagraphChange = (e) => {
    onChange(field.field_id, e.target.value);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        as="textarea"
        rows={4}
        value={(value !== undefined && value !== null) ? value : ''}
        onChange={handleParagraphChange}
        isInvalid={!!error}
        placeholder={field.placeholder}
        required={field.required}
        disabled={disabled}
      />
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Phone Field Component
export const PhoneField = ({ field, value, onChange, error }) => {
  const handlePhoneChange = (e) => {
    // Basic phone number formatting
    let phoneNumber = e.target.value.replace(/\D/g, '');
    if (phoneNumber.length >= 10) {
      phoneNumber = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    onChange(field.field_id, phoneNumber);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaPhone className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        type="tel"
        value={(value !== undefined && value !== null) ? value : ''}
        onChange={handlePhoneChange}
        isInvalid={!!error}
        placeholder={field.placeholder || "(123) 456-7890"}
        required={field.required}
        maxLength={field.max_length}
      />
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// URL Field Component
export const UrlField = ({ field, value, onChange, error }) => {
  const handleUrlChange = (e) => {
    onChange(field.field_id, e.target.value);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaLink className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        type="url"
        value={(value !== undefined && value !== null) ? value : ''}
        onChange={handleUrlChange}
        isInvalid={!!error}
        placeholder={field.placeholder || "https://example.com"}
        required={field.required}
      />
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Barcode Field Component
export const BarcodeField = ({ field, value, onChange, error }) => {
  const [barcodeValue, setBarcodeValue] = useState(value || '');
  const [generatedBarcode, setGeneratedBarcode] = useState('');

  const generateBarcode = () => {
    // Simple barcode generation (you'd want to use a proper barcode library)
    if (barcodeValue) {
      // This is a placeholder - you'd integrate with a barcode library like JsBarcode
      const barcode = `|||${barcodeValue}|||`;
      setGeneratedBarcode(barcode);
      onChange(field.field_id, barcodeValue);
    }
  };

  const handleInputChange = (e) => {
    setBarcodeValue(e.target.value);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaQrcode className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <div className="d-flex">
        <Form.Control
          type="text"
          value={barcodeValue}
          onChange={handleInputChange}
          isInvalid={!!error}
          placeholder={field.placeholder || "Enter value to generate barcode"}
          required={field.required}
        />
        <Button 
          variant="outline-primary" 
          className="ms-2"
          onClick={generateBarcode}
          disabled={!barcodeValue}
        >
          Generate
        </Button>
      </div>
      {generatedBarcode && (
        <div className="mt-2 p-2 border">
          <div className="barcode-display text-center">
            <code>{generatedBarcode}</code>
          </div>
        </div>
      )}
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Diagram Field Component (placeholder for future diagram integration)
export const DiagramField = ({ field, value, onChange, error }) => {
  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaImage className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Card>
        <Card.Body>
          <Alert variant="info">
            <strong>Diagram Field</strong><br />
            This field type is available for future diagram annotation features.
            Currently displays as informational content.
          </Alert>
          {field.help_text && (
            <div className="text-muted">{field.help_text}</div>
          )}
        </Card.Body>
      </Card>
      {error && <div className="text-danger mt-2">{error}</div>}
    </Form.Group>
  );
};

// Enhanced Image Upload Field
export const ImageUploadField = ({ field, value, onChange, error }) => {
  const [preview, setPreview] = useState(value || null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        onChange(field.field_id, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setPreview(null);
    onChange(field.field_id, '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        <FaImage className="me-2" />
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        isInvalid={!!error}
      />
      {preview && (
        <div className="mt-2">
          <img 
            src={preview} 
            alt="Preview" 
            style={{ maxWidth: '200px', maxHeight: '200px' }}
            className="border rounded"
          />
          <br />
          <Button variant="outline-danger" size="sm" className="mt-2" onClick={clearImage}>
            Remove Image
          </Button>
        </div>
      )}
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Enhanced Document Upload Field
export const DocumentUploadField = ({ field, value, onChange, error }) => {
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB limit for documents)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      setFileName(file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        onChange(field.field_id, {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          data: e.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const clearFile = () => {
    setFileName('');
    onChange(field.field_id, '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <Form.Control
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        isInvalid={!!error}
        accept=".pdf,.doc,.docx,.txt,.rtf"
      />
      {fileName && (
        <div className="mt-2">
          <Alert variant="success">
            <strong>Selected:</strong> {fileName}
            <Button variant="outline-danger" size="sm" className="ms-2" onClick={clearFile}>
              Remove
            </Button>
          </Alert>
        </div>
      )}
      {field.help_text && (
        <Form.Text className="text-muted">{field.help_text}</Form.Text>
      )}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

// Formula Field Component
export const FormulaField = ({ field, value, onChange, error }) => {
  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      <div className="formula-field-container">
        <div className="input-group">
          {field.prefix && (
            <span className="input-group-text">{field.prefix}</span>
          )}
          <Form.Control
            type="text"
            value={value !== undefined && value !== null && value !== '' ? value : ''}
            readOnly
            disabled
            className="bg-light"
            placeholder="No value"
          />
          {field.suffix && (
            <span className="input-group-text">{field.suffix}</span>
          )}
        </div>

        {field.show_formula && field.formula_expression && (
          <Form.Text className="text-muted font-monospace d-block mt-1">
            Formula: {field.formula_expression}
          </Form.Text>
        )}

        {field.help_text && (
          <Form.Text className="text-muted d-block mt-1">
            {field.help_text}
          </Form.Text>
        )}

        {error && (
          <Form.Text className="text-danger d-block mt-1">
            {error}
          </Form.Text>
        )}
      </div>
    </Form.Group>
  );
};

// Field Type Mapper
export const getEnhancedFieldComponent = (fieldType) => {
  const fieldComponents = {
    'time': TimeField,
    'datetime': DateTimeField,
    'signature': SignatureField,
    'header': HeaderField,
    'markdown': MarkdownField,
    'paragraph': ParagraphField,
    'phone': PhoneField,
    'url': UrlField,
    'barcode': BarcodeField,
    'diagram': DiagramField,
    'image_upload': ImageUploadField,
    'document_upload': DocumentUploadField,
    'formula': FormulaField,
  };

  return fieldComponents[fieldType] || null;
};